import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Copy, RefreshCw } from 'lucide-react';
import { updateUser, apiResetPassword } from '../../services/auth';

const Profile = ({ editEnabled }) => {
  const { theme } = useTheme();
  const { user, setUser, logout } = useAuth();
  const [name, setName] = useState(user.name);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleNameUpdate = async () => {
    try {
      await updateUser(user.userId, name);
      user.name = name;
      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      setIsEditing(false);
      setSuccess('Name updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const handlePasswordReset = async () => {
    try {
      await apiResetPassword(currentPassword, newPassword);
      setSuccess('Password updated successfully');
      setTimeout(() => {
        setSuccess('');
        setNewPassword('');
        setCurrentPassword('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
    setSuccess('Password copied to clipboard');
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <div className={`p-4 md:p-6 w-full max-w-2xl mx-auto ${theme === 'light' ? 'text-gray-800' : 'text-gray-200'}`}>
      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Profile Settings</h2>

      {/* Name Section */}
      <div className="mb-4 md:mb-6">
        <label className="block text-sm font-medium mb-2">Name</label>
        <div className="flex flex-col sm:flex-row gap-2">
          {isEditing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full sm:flex-1 px-3 py-2 rounded-lg border ${
                theme === 'light' 
                  ? 'border-gray-300 bg-white text-gray-900' 
                  : 'border-gray-700 bg-gray-800 text-gray-100'
              }`}
            />
          ) : (
            <div className={`w-full sm:flex-1 px-3 py-2 rounded-lg border ${
              theme === 'light' 
                ? 'border-gray-300 bg-gray-50 text-gray-900' 
                : 'border-gray-700 bg-gray-800 text-gray-100'
            }`}>
              {user.name}
            </div>
          )}
          <button
            onClick={isEditing ? handleNameUpdate : () => setIsEditing(true)}
            className={`w-full sm:w-auto px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center justify-center gap-1 whitespace-nowrap ${
              theme === 'light'
                ? 'border-purple-500/30 text-purple-500 bg-purple-500/10'
                : 'border-purple-500/30 text-purple-500 bg-purple-500/10'
            }`}
          >
            {isEditing ? 'Save' : 'Edit'}
          </button>
        </div>
      </div>

      {/* Email Section */}
      <div className="mb-4 md:mb-6">
        <label className="block text-sm font-medium mb-2">Email</label>
        <div className={`px-3 py-2 rounded-lg border ${
          theme === 'light' 
            ? 'border-gray-300 bg-gray-50 text-gray-900' 
            : 'border-gray-700 bg-gray-800 text-gray-100'
        }`}>
          {user.email}
        </div>
      </div>

      {/* Password Reset Section */}
      <div className="mb-4 md:mb-6">
        <label className="block text-sm font-medium mb-2">Reset Password</label>
        <div className="flex flex-col gap-2">
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Current password"
            className={`w-full px-3 py-2 rounded-lg border ${
              theme === 'light' 
                ? 'border-gray-300 bg-white text-gray-900' 
                : 'border-gray-700 bg-gray-800 text-gray-100'
            }`}
          />
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              className={`w-full sm:flex-1 px-3 py-2 rounded-lg border ${
                theme === 'light' 
                  ? 'border-gray-300 bg-white text-gray-900' 
                  : 'border-gray-700 bg-gray-800 text-gray-100'
              }`}
            />
            <div className="flex gap-2 justify-end sm:justify-start">
              <button
                onClick={generatePassword}
                className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                  theme === 'light'
                    ? 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                    : 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                }`}
                title="Generate password"
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={copyToClipboard}
                className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                  theme === 'light'
                    ? 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                    : 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                }`}
                title="Copy to clipboard"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={handlePasswordReset}
          disabled={!currentPassword || !newPassword}
          className={`mt-2 px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap w-full justify-center ${
            currentPassword && newPassword
              ? 'border-purple-500/30 text-purple-500 bg-purple-500/10'
              : 'border-gray-500/30 text-gray-500 cursor-not-allowed'
          }`}
        >
          Update Password
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 rounded-lg bg-red-100 text-red-700 mb-4 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="p-3 rounded-lg bg-green-100 text-green-700 mb-4 text-sm">
          {success}
        </div>
      )}

      {/* Logout Button */}
      <button
        onClick={logout}
        className={`w-full px-4 py-2 text-sm font-medium border backdrop-blur-sm rounded-full transition-all duration-200 ${
          theme === 'light'
            ? 'border-red-500/30 text-red-500 bg-red-500/10 hover:bg-red-500/20'
            : 'border-red-500/30 text-red-500 bg-red-500/10 hover:bg-red-500/20'
        }`}
      >
        Logout
      </button>
    </div>
  );
};

export default Profile;
